import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

// local imports
import { IDashboardSummary, IOnCampusStatsSummary, ISummaryRequest } from 'types/DashboardType'
import { IAllLocationList } from 'types/LocationType'
import DashboardService from 'service/DashboardService'
import LocationService from 'service/LocationService'
import PlanService from 'service/PlanService'

// Util Functions
const fetchSumOfKey = (arr: any[] | undefined, property: string): number => {
  if (!arr || !arr.length) return 0
  return arr.map((item: any) => item[property]).reduce((prev: any, curr: any) => prev + curr, 0)
}
const percentage = (partialValue: number, total: number) => {
  if (total === 0) return 0
  return (100 * partialValue) / total
}
// Util Functions

const useChartSummary = (userInfo: any, firmList: any) => {
  const { t } = useTranslation()
  const initialCampusSummaryData = useRef<IOnCampusStatsSummary>()
  const options = [
    { name: t('tabs.employee-notifications'), value: 'work' },
    { name: t('tabs.on-campus-services'), value: 'on_campus' },
  ]
  const [locationList, setLocationList] = useState(
    userInfo !== 'LocationAdmin' || firmList.length > 1
      ? [{ id: null, name: `(${t('input-texts.all-locations')})` }]
      : []
  )
  const [selectedCompany, setSelectedCompany] = useState<any>(
    userInfo !== 'LocationAdmin' || firmList.length > 1 ? [...firmList] : [firmList[0]]
  )
  const initialSelectedLocation =
    userInfo !== 'LocationAdmin' || firmList.length > 1
      ? { id: null, name: `(${t('input-texts.all-locations')})` }
      : locationList[0]
  const [selectedLocation, setSelectedLocation] = useState<any>(initialSelectedLocation)

  const [showLoader, setShowLoader] = useState<boolean>(false)
  const [pageLoading, setPageLoading] = useState(false)
  const [showError, setShowError] = useState<boolean>(false)
  const [value, setValue] = useState(options[0].value)
  const [summary, setSummary] = useState<IDashboardSummary | undefined>()
  const [campusSummary, setCampusSummary] = useState<IOnCampusStatsSummary>({
    cameCount: 0,
    camePercentage: 0,
    firmStatistics: [],
    notCameCount: 0,
    notCamePercentage: 0,
    totalCount: 0,
  })

  /* ####### GET Dashboard Summary Data ####### */
  async function getSummaryData(companies: any, location: any, loadingFunc: Dispatch<SetStateAction<boolean>>) {
    const request1 = {
      firmList: companies.map((item: any) => item.id),
      hasSharedOffice: null,
    }
    const request2: ISummaryRequest = {
      locationId: location && location.id,
      firmIdList: companies.map((item: any) => item.id),
    }
    loadingFunc(true)
    Promise.allSettled([
      DashboardService.getDashboardSummary(request2),
      LocationService.GetAllLocationByFirmAndType(request1),
    ])
      .then(([dashboardSummaries, allLocations]) => {
        dashboardSummaries.status === 'fulfilled' ? setSummary(dashboardSummaries.value.data) : setShowError(true)
        if (allLocations.status === 'fulfilled') setLocationList(allLocations.value.data)
      })
      .catch((_e) => console.log(_e))
      .finally(() => loadingFunc(false))
  }

  /* ####### GET Campus Services Summary Data ####### */
  function getCampusServiceSummary(companies: any) {
    PlanService.getCampusServiceStatsSummary({
      startDate: '2023-01-01',
      endDate: new Date().toISOString(),
      firmIds: companies.map((item: any) => item.id),
    })
      .then((res) => {
        setCampusSummary(res.data)
        initialCampusSummaryData.current = res.data
      })
      .catch((err) => console.log(err))
  }

  useEffect(() => {
    getSummaryData(selectedCompany, selectedLocation, setPageLoading)
    getCampusServiceSummary(selectedCompany)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* Location Dropdown handle change function */
  const getDashboardSummary = (companies: any, location: any) => {
    const request: ISummaryRequest = {
      locationId: location && location.id,
      firmIdList: companies.map((item: any) => item.id),
    }
    setShowLoader(true)
    DashboardService.getDashboardSummary(request)
      .then((resp: any) => setSummary(resp.data))
      .catch((_e) => setShowError(true))
      .finally(() => setShowLoader(false))
  }

  const updateCampusSummary = (firmIds: number[], selectedLocation: IAllLocationList | { id: null; name: string }) => {
    const filteredStatistics = (initialCampusSummaryData?.current?.firmStatistics || []).filter(
      ({ firmId, locationId }) => {
        if (selectedLocation.id !== null) return firmIds.includes(firmId) && locationId === selectedLocation.id
        else return firmIds.includes(firmId)
      }
    )
    const totalCount = filteredStatistics.reduce((total, data) => total + data.totalCount, 0)
    setCampusSummary((prev) => ({ ...prev, totalCount: totalCount, firmStatistics: filteredStatistics }))
  }

  /* Firm Dropdown handle change function */
  const handleFirmChange = (companies: any) => {
    const firmIds = companies?.map((company: any) => company.id)
    setSelectedLocation(initialSelectedLocation)
    getSummaryData(companies, initialSelectedLocation, setShowLoader)
    updateCampusSummary(firmIds, initialSelectedLocation)
  }

  /* Location Dropdown handle change function */
  const handleLocationChange = (companies: any, location: any) => {
    const firmIds = companies?.map((company: any) => company.id)
    getDashboardSummary(companies, location)
    updateCampusSummary(firmIds, location)
  }

  /* Campus Services */
  const campusSummaryTotal = campusSummary?.totalCount || 0
  const healthCount = fetchSumOfKey(
    campusSummary?.firmStatistics.filter((it) => it.campusServiceType === 1),
    'totalCount'
  )
  const coiffeurCount = fetchSumOfKey(
    campusSummary?.firmStatistics.filter((it) => it.campusServiceType === 2),
    'totalCount'
  )
  const gymCount = fetchSumOfKey(
    campusSummary?.firmStatistics.filter((it) => it.campusServiceType === 3),
    'totalCount'
  )
  const lactationCount = fetchSumOfKey(
    campusSummary?.firmStatistics.filter((it) => it.campusServiceType === 4),
    'totalCount'
  )

  const campusSummaryObj = {
    campusSummaryTotal,
    healthCount,
    healthPercent: percentage(healthCount, campusSummaryTotal),
    coiffeurCount,
    coiffeurPercent: percentage(coiffeurCount, campusSummaryTotal),
    gymCount,
    gymPercent: percentage(gymCount, campusSummaryTotal),
    lactationCount,
    lactationPercent: percentage(lactationCount, campusSummaryTotal),
  }

  return {
    t,
    handleFirmChange,
    handleLocationChange,
    campusSummaryObj,
    summary,
    showError,
    showLoader,
    pageLoading,
    options,
    selectedCompany,
    setSelectedCompany,
    locationList,
    setSelectedLocation,
    selectedLocation,
    value,
    setValue,
  }
}

export default useChartSummary
